"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import type { AuthedFile } from "@/lib/api";

import { closePrintTab, deliverPdf, openPrintTab, type PdfAction, type PdfDelivery } from "./pdf-delivery";
import { pdfRefusalOf, type PdfRefusal } from "./refusal";

/** Where a problem set PDF stands: being made, saved or printed, or refused and why. */
export type PdfRun =
  | { status: "idle" }
  | { status: "making"; action: PdfAction }
  | { status: "done"; action: PdfAction; delivery: PdfDelivery; filename: string }
  | { status: "error"; action: PdfAction; refusal: PdfRefusal };

export type PdfRunRequest = {
  /** Reads the PDF: a contest route's `GET`, or the author set's `POST`. */
  load: () => Promise<AuthedFile>;
  action: PdfAction;
  /** The line the print tab shows while the PDF is made. */
  waiting: string;
};

/**
 * One PDF at a time from one control (`EX-PDF02`, lifted for `EX-PDF03`).
 * Print opens its tab inside the press (a browser blocks a tab opened later),
 * and a refusal closes it again. `onDelivered` runs after a PDF was saved or
 * printed (the console refreshes its downloads list).
 */
export function usePdfRun(onDelivered?: () => void) {
  const [run, setRun] = useState<PdfRun>({ status: "idle" });
  const delivered = useRef(onDelivered);
  useEffect(() => {
    delivered.current = onDelivered;
  }, [onDelivered]);

  const start = useCallback(async (request: PdfRunRequest) => {
    const tab = request.action === "print" ? openPrintTab(request.waiting) : null;
    setRun({ status: "making", action: request.action });
    try {
      const file = await request.load();
      const delivery = deliverPdf(file, request.action, tab);
      setRun({ status: "done", action: request.action, delivery, filename: file.filename });
      delivered.current?.();
    } catch (error) {
      closePrintTab(tab);
      setRun({ status: "error", action: request.action, refusal: pdfRefusalOf(error) });
    }
  }, []);
  const reset = useCallback(() => setRun({ status: "idle" }), []);

  return { run, start, reset };
}
